import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { LocateFixed } from "lucide-react";
import { toast } from "sonner";

const NAIROBI = [-1.2833, 36.7930];

const pinIcon = L.divIcon({
  className: "",
  html: '<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;background:linear-gradient(135deg,#CC1A00,#FF6600);transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 4px 10px rgba(0,0,0,.35)"></div>',
  iconSize: [30, 30],
  iconAnchor: [15, 30],
});

export const kmBetween = (a, b) => {
  const r = (d) => (d * Math.PI) / 180;
  const h = Math.sin(r(b.lat - a.lat) / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(r(b.lng - a.lng) / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
};

// Same rule as the server: the closest area containing the pin, else the closest area overall.
export const feeZoneFor = (pin, zones) => {
  if (!pin || !zones.length) return null;
  const byDist = [...zones].sort((a, b) => kmBetween(pin, a) - kmBetween(pin, b));
  return byDist.find((z) => kmBetween(pin, z) <= z.radius_km) || byDist[0];
};

function FlyToPin({ pin }) {
  const map = useMap();
  const first = useRef(true);
  useEffect(() => {
    if (pin && first.current) map.setView([pin.lat, pin.lng], 16);
    first.current = false;
  }, [pin, map]);
  return null;
}

function ClickToPin({ onPick }) {
  useMapEvents({ click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
}

function Locate({ onPin }) {
  const map = useMap();
  const locate = () => {
    if (!navigator.geolocation) { toast.error("Location is not supported on this device"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { const here = { lat: p.coords.latitude, lng: p.coords.longitude }; onPin(here); map.setView([here.lat, here.lng], 16); },
      () => toast.error("Could not get your location. Please tap the map instead."),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };
  return (
    <button type="button" onClick={locate} data-testid="use-my-location"
      className="absolute z-[500] bottom-3 right-3 bg-white rounded-full shadow-md px-3 py-2 text-xs font-semibold flex items-center gap-1.5 text-[#CC1A00] hover:bg-[#FFF1EE]">
      <LocateFixed size={14} /> Use my location
    </button>
  );
}

export default function LocationPicker({ pin, onPin }) {
  return (
    <div data-testid="location-picker">
      <div className="relative rounded-xl overflow-hidden border border-gray-200 h-64 z-0">
        <MapContainer center={pin ? [pin.lat, pin.lng] : NAIROBI} zoom={pin ? 16 : 13} className="h-full w-full" scrollWheelZoom={false}>
          <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FlyToPin pin={pin} />
          <ClickToPin onPick={onPin} />
          {pin && (
            <Marker position={[pin.lat, pin.lng]} icon={pinIcon} draggable
              eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); onPin({ lat: ll.lat, lng: ll.lng }); } }} />
          )}
          <Locate onPin={onPin} />
        </MapContainer>
      </div>
      <p data-testid="pin-status" className={`text-xs mt-2 ${pin ? "text-emerald-700" : "text-gray-500"}`}>
        {pin ? "Pin set. Drag it to fine-tune." : "Tap the map to drop a pin on your building."}
      </p>
    </div>
  );
}
